import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
// import { Auth } from 'aws-amplify';
import { Box, IconButton, TextField } from '@fairstone/ui/core';
import { t } from '@fairstone/ui/core/providers/Intl/locales/translate';
import { UNAUTHORIZED_EXCEPTION } from 'constant';
import useAuth from 'context/useAuth';
import useFormFields from 'hooks/useFormFields';

import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import EditProfileWrapper from 'components/forms/profile/EditProfileWrapper';
import CustomModal from 'components/modals/CustomModal';
import { PrimaryButtonSto } from 'components/sto';
import { AppRoutes } from 'routes';
import { isValidEmail } from 'utils/Validator';

import EmailVerification from '../../components/emailVerification/EmailVerification';

import styles from './ProfileEmailPage.module.scss';

type EmailForms = {
    email: {
        errorMessage: string;
        isValid: boolean;
        isChanged: boolean;
        value: string;
    };
    verificationCode: {
        errorMessage: string;
        value: string;
        isValid: boolean | null;
    };
};

type CustomModalType = {
    modalType: string;
    okText: string;
    onOk: null | (() => void);
    title: string;
    visible: boolean;
};

const ProfileEmailPage = (): React.ReactElement => {
    const { formatMessage, locale } = useIntl();
    const history = useHistory();
    const { userProfile } = useAuth();
    const [apiError, setApiError] = useState<string | null>(null);
    const [codeResent, setCodeResent] = useState<boolean>(false);
    const [displayOtpModal, setDisplayOtpModal] = useState<boolean>(false);
    const emailRef = useRef<HTMLInputElement | null>(null);
    const [enableEdit, setEnableEdit] = useState<boolean>(false);
    const [codeSent, setCodeSent] = useState<boolean>(false);
    const [isConfirming, setIsConfirming] = useState<boolean>(false);
    const [isSendingCode, setIsSendingCode] = useState<boolean>(false);
    const { formFields, setFormField } = useFormFields<EmailForms>({
        email: {
            errorMessage: '',
            isChanged: false,
            isValid: userProfile.email && isValidEmail(userProfile.email),
            value: userProfile.email,
        },
        verificationCode: {
            errorMessage: '',
            isValid: null,
            value: '',
        },
    });
    const { email, verificationCode } = formFields;
    const initialModalValues: CustomModalType = {
        modalType: '',
        okText: '',
        onOk: null,
        title: '',
        visible: false,
    };
    const [modal, setModal] = useState<CustomModalType>(initialModalValues);

    const handleApiError = (error: any) => {
        switch (error.name) {
            case UNAUTHORIZED_EXCEPTION:
                setApiError('general.api.error.invalid.verification.code');
                break;
            default:
                setModal({
                    modalType: 'fail',
                    okText: 'general.ok',
                    onOk: handleGoBackOnError,
                    title: 'general.api.error.unable.to.complete',
                    visible: true,
                });
                break;
        }
    };

    const handleApiSuccess = () => {
        setModal({
            modalType: 'success',
            okText: 'general.ok',
            onOk: handleGoBackOnSuccess,
            title: 'pages.profile.edit.email.updated',
            visible: true,
        });
    };

    const handleBlurEmail = () => {
        if (email.isValid) setEnableEdit(false);
    };

    const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const isValid = value && isValidEmail(value);
        let errorMessage = '';
        if (!isValid) {
            errorMessage = !value ? 'pages.profile.edit.error.blank.email' : 'pages.profile.edit.error.invalid.email';
        }
        setFormField('email', {
            errorMessage: errorMessage,
            isChanged: value !== userProfile.email,
            isValid: isValid,
            value: value,
        });
    };

    const handleChangeCode = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const isValid = value.length === 6;
        let errorMessage = '';
        if (!isValid) {
            errorMessage = !value.length ? 'general.enter.verification.code' : 'general.verification.code.length';
        }

        setFormField('verificationCode', {
            errorMessage,
            isValid,
            value,
        });
    };

    const handleGoBackOnError = () => {
        setModal(initialModalValues);
    };

    const handleGoBackOnSuccess = () => {
        history.push(`/${locale}/${formatMessage({ id: 'tab.label.profile' }).toLowerCase()}`);
    };

    const handleGoBackOnVerification = () => {
        setCodeSent(false);
        setIsSendingCode(false);
        setFormField('verificationCode', {
            errorMessage: '',
            isValid: null,
            value: '',
        });
    };

    const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
        try {
            event.preventDefault();
            setIsSendingCode(true);
            // const user = Auth.currentAuthenticatedUser();
            // const res = await Auth.updateUserAttributes(user, { email: email.value });
            // setUserProfile((prevState: any) => ({
            //   ...prevState,
            //   email: res.find((o: any) => o.fieldToUpdate === 'email').fieldData,
            // }));
            setCodeSent(true);
        } catch (error) {
            setIsSendingCode(false);
            handleApiError(error);
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        try {
            event.preventDefault();
            setIsConfirming(true);
            // await Auth.verifyCurrentUserAttributeSubmit("email", verificationCode.value);
            handleApiSuccess();
        } catch (error) {
            setIsConfirming(false);
            handleApiError(error);
        }
    };

    const handleResend = async () => {
        try {
            setDisplayOtpModal(true);
            // await Auth.verifyCurrentUserAttribute('email');
            setCodeResent(true);
            setFormField('verificationCode', {
                errorMessage: '',
                isValid: null,
                value: '',
            });
        } catch (error) {
            setCodeResent(false);
            handleApiError(error);
        }
    };

    useEffect(() => {
        if (enableEdit) emailRef?.current?.focus();
    }, [enableEdit]);

    return (
        <EditProfileWrapper
            goBack={codeSent ? handleGoBackOnVerification : AppRoutes.profile}
            title={codeSent ? 'pages.profile.title.email.verification' : 'pages.profile.title.email.change'}
        >
            <Box className={styles.editProfileContainer}>
                {codeSent ? (
                    <EmailVerification
                        apiError={apiError}
                        codeResent={codeResent}
                        disabled={isConfirming}
                        displayOtpModal={displayOtpModal}
                        email={email.value}
                        handleChange={handleChangeCode}
                        handleResend={handleResend}
                        handleSubmit={handleSubmit}
                        setDisplayOtpModal={setDisplayOtpModal}
                        verificationCode={verificationCode}
                    />
                ) : (
                    <form className={styles.editProfileContent} noValidate onSubmit={handleSave}>
                        <TextField
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                className: styles.textInput,
                                endAdornment: !enableEdit && (
                                    <IconButton
                                        onClick={() => setEnableEdit(true)}
                                        title={formatMessage({ id: 'pages.profile.edit' })}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                ),
                                required: true,
                            }}
                            aria-required="true"
                            autoFocus
                            className={styles.textField}
                            error={!email.isValid}
                            fullWidth
                            helperText={!email.isValid && email.errorMessage && t(email.errorMessage)}
                            inputProps={{
                                disabled: !enableEdit,
                            }}
                            inputRef={emailRef}
                            label={t('pages.profile.edit.email.label')}
                            name="email"
                            onBlur={handleBlurEmail}
                            onChange={handleChangeEmail}
                            value={email.value}
                            variant="standard"
                        />
                        <PrimaryButtonSto
                            disabled={!email.isValid || !email.isChanged || isSendingCode}
                            labelKey="general.save"
                            type="submit"
                        />
                    </form>
                )}
                <CustomModal
                    modalType={modal.modalType}
                    okText={modal.okText}
                    onOk={modal.onOk}
                    visible={modal.visible}
                >
                    <p>{t(modal.title)}</p>
                </CustomModal>
            </Box>
        </EditProfileWrapper>
    );
};

export default ProfileEmailPage;
